import type { ReactNode } from 'react';
import { Badge, cn, type BadgeProps } from '@ludolio/shared-ui';
import { BUILD_STATUS_LABELS, RELEASE_STATUS_LABELS, TEAM_ROLE_LABELS } from '@/lib/format';
import type { LabBuildStatus, LabReleaseStatus, LabTeamRole } from '@/lib/types';

const RELEASE_VARIANTS: Record<LabReleaseStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  in_review: 'info',
  changes_requested: 'warning',
  published: 'success',
  superseded: 'outline',
  withdrawn: 'destructive',
};

const BUILD_VARIANTS: Record<LabBuildStatus, BadgeProps['variant']> = {
  uploading: 'info',
  queued: 'info',
  scanning: 'info',
  passed: 'success',
  failed: 'destructive',
  error: 'warning',
};

export function ReleaseStatusBadge({ status }: { status: LabReleaseStatus }) {
  return <Badge variant={RELEASE_VARIANTS[status]}>{RELEASE_STATUS_LABELS[status]}</Badge>;
}

export function BuildStatusBadge({ status }: { status: LabBuildStatus }) {
  return <Badge variant={BUILD_VARIANTS[status]}>{BUILD_STATUS_LABELS[status]}</Badge>;
}

const TEAM_ROLE_VARIANTS: Record<LabTeamRole, BadgeProps['variant']> = {
  owner: 'info',
  manager: 'secondary',
  member: 'outline',
};

export function TeamRoleBadge({ role }: { role: LabTeamRole }) {
  return <Badge variant={TEAM_ROLE_VARIANTS[role]}>{TEAM_ROLE_LABELS[role]}</Badge>;
}

export function Cover({ src, title, className }: { src: string | null; title: string; className?: string }) {
  return (
    <div className={cn('relative aspect-video w-full overflow-hidden bg-muted', className)}>
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[hsl(var(--color-primary))]/15 to-[hsl(var(--color-primary))]/5">
          <span className="text-3xl font-semibold text-[hsl(var(--color-primary))]/60" aria-hidden="true">
            {title.trim().charAt(0).toUpperCase() || 'L'}
          </span>
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed py-16 text-center">
      <p className="text-sm text-muted-foreground">{title}</p>
      {action}
    </div>
  );
}

export function SectionTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex min-h-9 items-center justify-between gap-4">
      <h2 className="text-base font-semibold">{children}</h2>
      {actions}
    </div>
  );
}
